import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { traceViewerStore } from './trace-viewer.store';
import { formatDuration, getDurationColorClass } from './trace-viewer.utils';

import type { TraceSpan } from './trace-viewer.types';

interface SpanNodeProps {
  span: TraceSpan;
  depth: number;
  traceStartTime: number;
  traceDuration: number;
}

export const SpanNode = observer(
  ({ span, depth, traceStartTime, traceDuration }: SpanNodeProps) => {
    const store = traceViewerStore;
    const hasChildren = span.children.length > 0;
    const isExpanded = store.expandedSpanIds.has(span.id);
    const isSelected = store.selectedSpanId === span.id;
    const spanIdsMatchingSearch = store.spanIdsMatchingSearch;
    const isMatchingSearchDirectly = spanIdsMatchingSearch.has(span.id);

    const handleSelect = () => {
      store.selectSpan(span.id);
      if (hasChildren) {
        store.toggleExpanded(span.id);
      }
    };

    const durationMs = span.duration / 1000;
    const durationColorClass = getDurationColorClass(durationMs);

    const arr = Array(depth).fill(0);
    return (
      <YStack className={cn('bg-primary/10 hover:bg-primary/15')}>
        <XStack
          className={cn(
            'group relative cursor-pointer hover:bg-surface-hover transition-colors rounded-sm',
            'border-b border-border',
            isSelected && 'bg-primary/10 hover:bg-primary/15',
            isMatchingSearchDirectly && 'bg-green-400/10 hover:bg-green-400/15'
          )}
          onClick={handleSelect}
        >
          <XStack className="flex-1 items-center gap-2 py-1.5 px-2 min-w-0 relative">
            <div style={{ width: `${depth * 20}px` }} className="shrink-0" />
            {arr.map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-[calc(100%+1px)] w-0.5 bg-border-strong absolute bottom-0  '
                )}
                style={{
                  left: `calc(1.25rem + ${index * 20}px)`,
                  top: '-0.75rem',
                }}
              />
            ))}
            {depth ? (
              <Icons.squareFunction
                className={cn(
                  'h-3 w-3 text-foreground-subtle',
                  isSelected ? 'text-foreground' : 'text-foreground-muted'
                )}
              />
            ) : (
              <Icons.doorOpen
                className={cn(
                  'h-3 w-3 text-foreground-subtle',
                  isSelected ? 'text-foreground' : 'text-foreground-muted'
                )}
              />
            )}
            <span
              className={cn(
                'text-sm font-mono truncate',
                isSelected
                  ? 'text-foreground font-medium'
                  : 'text-foreground-muted'
              )}
              title={span.operationName}
            >
              {span.operationName}
            </span>
            <span
              className={cn(
                'shrink-0 text-xs font-mono px-1.5 py-0.5 rounded',
                'bg-surface',
                durationColorClass
              )}
            >
              {formatDuration(span.duration)}
            </span>
            {hasChildren && (
              <span className="shrink-0 text-xs text-foreground-subtle">
                ({span.children.length})
              </span>
            )}
            {hasChildren && (
              <XStack className="flex-1 items-end justify-end">
                <Icons.chevronRight
                  className={cn(
                    'h-3 w-3 text-foreground-subtle transition-transform',
                    isExpanded && 'rotate-90'
                  )}
                />
              </XStack>
            )}
          </XStack>
        </XStack>
        {hasChildren && isExpanded && (
          <YStack>
            {span.children.map((child) => (
              <SpanNode
                key={child.id}
                span={child}
                depth={depth + 1}
                traceStartTime={traceStartTime}
                traceDuration={traceDuration}
              />
            ))}
          </YStack>
        )}
      </YStack>
    );
  }
);
